/**
 * Financial Close Setup store (Location, Task Library, Users, custom fields).
 *
 * Naming — actions/getters use **TaskTemplate** for Setup Task Library master
 * checklist items that seed cycles. They are not cycle Tasklist tasks.
 * State keys may still say `templates` / `activeTemplate` for brevity.
 * API paths still use `/masters/checklist/*`.
 * User Management URLs match Setup/User `userConstants.API` (do not invent).
 */
import Vue from 'vue';
import { DUE_FIELD_KEYS } from '@/modules/financialclose/utils/constants';
import { mergeLocationTypes } from '@/modules/financialclose/utils/locationMasterType';

const SETUP_API = {
  LOCATION_GET: '/v2/financialclose/location/get',
  LOCATION_FORMSELECTS: '/v2/financialclose/location/formselects',
  LOCATION_ADD: '/v2/financialclose/location/add',
  LOCATION_EDIT: (id) => `/v2/financialclose/location/edit/${id}`,
  LOCATION_ENABLE: (id) => `/v2/financialclose/location/enable/${id}`,
  LOCATION_DISABLE: (id) => `/v2/financialclose/location/disable/${id}`,
  THRESHOLD_GET: '/v2/financialclose/masters/threshold/get',
  THRESHOLD_ADD: '/v2/financialclose/masters/threshold/add',
  THRESHOLD_EDIT: (id) => `v2/financialclose/masters/threshold/edit/${id}`,
  CHECKLIST_GET: '/v2/financialclose/masters/checklist/get',
  CHECKLIST_GET_ONE: (id) => `/v2/financialclose/masters/checklist/get/${id}`,
  CHECKLIST_GET_DETAILS: (id) => `/v2/financialclose/masters/checklist/getdetails/${id}`,
  CHECKLIST_GET_TASKS_FOR_LINK: '/v2/financialclose/masters/checklist/getTasksForLink',
  CHECKLIST_FORMDATA: '/v2/financialclose/masters/checklist/getformdata',
  CHECKLIST_ADD: '/v2/financialclose/masters/checklist/add',
  CHECKLIST_EDIT: (id) => `/v2/financialclose/masters/checklist/edit/${id}`,
  CHECKLIST_ENABLE: (id) => `/v2/financialclose/masters/checklist/enable/${id}`,
  CHECKLIST_DISABLE: (id) => `/v2/financialclose/masters/checklist/disable/${id}`,
  CHECKLIST_BULK_UPDATE: '/v2/financialclose/masters/checklist/bulkUpdateMasterTaskList',
  CHECKLIST_BULK_DUE_DAYS: '/v2/financialclose/masters/checklist/bulkupdateduedays',
  CHECKLIST_MANAGE_LOCATION: '/v2/financialclose/masters/checklist/managelocation',
  CHECKLIST_MANAGE_USER: '/v2/financialclose/masters/checklist/managechecklistuser',
  CHECKLIST_BULK_ADD: '/v2/financialclose/masters/checklist/bulkadd',
  CHECKLIST_BULK_VALIDATE: '/v2/financialclose/masters/checklist/bulkvalidate',
  READ_COLUMN: '/v2/financialclose/masters/checklist/readColumn/',
  CREATE_COLUMN: '/v2/financialclose/masters/checklist/createColumn',
  EDIT_COLUMN: '/v2/financialclose/masters/checklist/editColumn/',
  COA_GET: '/v2/financialclose/masters/chartofaccount/get',
  /** Setup Users (userConstants.API) */
  USER_MANAGEMENT_LIST: '/v2/financialclose/masters/usermanagement/list',
  USER_MANAGEMENT_GET: (id) => `/v2/financialclose/masters/usermanagement/get/${id}`,
  USER_MANAGEMENT_ADD: '/v2/financialclose/masters/usermanagement/add',
  USER_MANAGEMENT_EDIT: (id) => `/v2/financialclose/masters/usermanagement/edit/${id}`,
  USER_MANAGEMENT_SET_STATUS: (id) =>
    `/v2/financialclose/masters/usermanagement/setstatus/${id}`,
  ENTITY_USERS: '/v2/client/getAllUsersForEntity',
};

/** Map raw cycle_type into CycleTypeTabs buckets (unknown → OTHERS). */
function resolveCycleBucket(raw) {
  const key = String(raw || 'FBC').trim().toUpperCase() || 'FBC';
  if (key === 'FBC' || key === 'RM' || key === 'IA') return key;
  return 'OTHERS';
}

function emptyCycleCounts() {
  return { FBC: 0, RM: 0, IA: 0, OTHERS: 0 };
}

function buildCycleCounts(items = []) {
  const counts = emptyCycleCounts();
  (items || []).forEach((item) => {
    const key = resolveCycleBucket(item?.cycle_type || item?.cycleType);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

/** Empty Add/Edit Task Template dialog state (Task Library). */
function emptyAddTaskTemplateDialog() {
  return { open: false, mode: 'add', taskType: null, templateId: null };
}

/** Empty custom field dialog state (Task Library). */
function emptyCustomFieldDialog() {
  return { open: false, mode: 'add', column: null };
}

/** Empty Setup Location dialog state. */
function emptyLocationDialog() {
  return { open: false, mode: 'add', locationId: null };
}

/** Checklist form data scoped to the active Setup library cycle. */
function checklistFormDataPayload(cycleType) {
  return { cycle_type: resolveCycleBucket(cycleType) };
}

/** Unwrap checklist getformdata payload (`[{ users, location }]` or plain object). */
function unwrapFormDataPayload(raw) {
  if (Array.isArray(raw)) return raw[0] || {};
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data[0] || {};
    if (raw.formdata && typeof raw.formdata === 'object') return raw.formdata;
    return raw;
  }
  return {};
}

/** Unique sorted string values from COA rows for a field. */
function uniqueCoaFieldValues(coaList = [], field) {
  const set = new Set();
  (coaList || []).forEach((row) => {
    const val = row?.[field];
    if (val != null && String(val).trim()) set.add(String(val).trim());
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * Map checklist getformdata category lists.
 * `categories` stays COA-only (account recon + bulk import checks).
 * `categoryAcc` is COA plus custom checklist values.
 */
function categoryMetaFromFormData(fd = {}, coaList = []) {
  const coaCategoryAcc = Array.isArray(fd?.coaCategoryAcc)
    ? fd.coaCategoryAcc
    : uniqueCoaFieldValues(coaList, 'category');
  const coaSubcategoryAcc = Array.isArray(fd?.coaSubcategoryAcc)
    ? fd.coaSubcategoryAcc
    : uniqueCoaFieldValues(coaList, 'subcategory');
  const categoryAcc = Array.isArray(fd?.categoryAcc)
    ? fd.categoryAcc
    : (Array.isArray(fd?.categories) ? fd.categories : coaCategoryAcc);
  const subcategoryAcc = Array.isArray(fd?.subcategoryAcc)
    ? fd.subcategoryAcc
    : (Array.isArray(fd?.subcategories) ? fd.subcategories : coaSubcategoryAcc);

  return {
    categories: coaCategoryAcc,
    subcategories: coaSubcategoryAcc,
    coaCategoryAcc,
    coaSubcategoryAcc,
    categoryAcc,
    subcategoryAcc,
  };
}

/** Unique tags collected from Task Library task templates. */
function uniqueTaskTemplateTags(templates = []) {
  const set = new Set();
  (templates || []).forEach((t) => {
    const tags = t?.tags || t?.frequency || [];
    (Array.isArray(tags) ? tags : [tags]).forEach((tag) => {
      const val = typeof tag === 'string' ? tag : tag?.name || tag?.label;
      if (val != null && String(val).trim()) set.add(String(val).trim());
    });
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Resolve Task Library template ref string from a selected template id. */
function templateRefFromSelection(templates = [], id) {
  const template = (templates || []).find((t) => String(t?._id) === String(id));
  const ref = template?.ref ?? template?.reference ?? id;
  return ref != null && ref !== '' ? String(ref) : '';
}

/** Throw when API envelope status is not success. */
function assertSuccess(response, fallbackMessage) {
  if (response?.data?.status === 'success') return response.data;
  const message = response?.data?.message || fallbackMessage || 'Request failed';
  const error = new Error(message);
  error.response = response;
  throw error;
}

/**
 * Due-only field errors are toasted by AddTemplateDialog after the inline message
 * is painted. Skip the generic failure toast here so save does not show two.
 * An empty fieldErrors object is not a due-only error.
 */
function isDueOnlyFieldError(fieldErrors) {
  if (!fieldErrors || typeof fieldErrors !== 'object' || Array.isArray(fieldErrors)) return false;
  const keys = Object.keys(fieldErrors);
  return keys.length > 0 && keys.every((key) => DUE_FIELD_KEYS.includes(key));
}

/**
 * Refresh category options after a write that already succeeded.
 * The refresh action snackbars and rethrows; swallow here so a stale
 * category list does not look like the write itself failed.
 */
async function refreshCategoriesAfterWrite(dispatch) {
  try {
    await dispatch('refreshChecklistFormCategories');
  } catch (error) {
    console.error('[setup]: category refresh after write failed', error);
  }
}

export default {
  namespaced: true,

  state: {
    setupMenuActive: 'location',
    cycleType: 'FBC',
    mainTab: 'templates',
    templates: [],
    templatesLoading: false,
    templatesInitialLoading: true,
    templateCountsByCycleType: { FBC: 0, RM: 0, IA: 0, OTHERS: 0 },
    customFieldCountsByCycleType: { FBC: 0, RM: 0, IA: 0, OTHERS: 0 },
    selectedTemplateIds: [],
    templateFilters: {},
    templateTableHeaders: [],
    customColumns: [],
    customFieldsLoading: false,

    locations: [],
    chartOfAccounts: [],
    users: [],
    formMeta: {
      countryList: [],
      locationTypes: [],
      thresholds: [],
      tags: [],
      assertions: [],
      categories: [],
      subcategories: [],
      coaCategoryAcc: [],
      coaSubcategoryAcc: [],
      categoryAcc: [],
      subcategoryAcc: [],
    },

    addTemplateDialog: emptyAddTaskTemplateDialog(),
    customFieldDialog: emptyCustomFieldDialog(),
    bulkImportOpen: false,

    activeTemplate: null,
    activeTemplateLoading: false,
    detailTab: 'assignments',

    locationRows: [],
    locationLoading: false,
    locationDialog: emptyLocationDialog(),

    /** Setup Users (user management) page */
    userManagementRows: [],
    userManagementLoading: false,
    userManagementLocations: [],
    userManagementEntityUsers: [],
  },

  mutations: {
    /** Active Setup side-nav item key. */
    setSetupMenuActive(state, value) {
      state.setupMenuActive = value;
    },
    /** Active Task Library cycle type filter (FBC / RM / IA). */
    setCycleType(state, value) {
      state.cycleType = resolveCycleBucket(value);
    },
    /** Task Library main tab (`templates` | custom fields). */
    setMainTab(state, value) {
      state.mainTab = value || 'templates';
    },
    /** Replace Task Library task template list. */
    setTemplates(state, list) {
      state.templates = Array.isArray(list) ? list : [];
    },
    /** Task template list fetch spinner. */
    setTemplatesLoading(state, loading) {
      state.templatesLoading = !!loading;
    },
    /** Task Library first-load skeleton flag. */
    setTemplatesInitialLoading(state, loading) {
      state.templatesInitialLoading = !!loading;
    },
    /** Per-cycle-type counts of Task Library task templates. */
    setTemplateCountsByCycleType(state, counts) {
      state.templateCountsByCycleType = {
        FBC: 0,
        RM: 0,
        IA: 0,
        OTHERS: 0,
        ...(counts || {}),
      };
    },
    /** Per-cycle-type counts of Task Library custom fields. */
    setCustomFieldCountsByCycleType(state, counts) {
      state.customFieldCountsByCycleType = {
        FBC: 0,
        RM: 0,
        IA: 0,
        OTHERS: 0,
        ...(counts || {}),
      };
    },
    /** Selected Task Library task template ids (bulk actions). */
    setSelectedTemplateIds(state, ids) {
      state.selectedTemplateIds = Array.isArray(ids) ? ids : [];
    },
    /** Clear Task Library bulk selection. */
    clearSelectedTemplateIds(state) {
      state.selectedTemplateIds = [];
    },
    /** Task Library table column filters. */
    setTemplateFilters(state, filters) {
      state.templateFilters = filters || {};
    },
    /** Replace Task Library custom field columns. */
    setCustomColumns(state, columns) {
      state.customColumns = Array.isArray(columns) ? columns : [];
    },
    /** Custom fields fetch spinner. */
    setCustomFieldsLoading(state, loading) {
      state.customFieldsLoading = !!loading;
    },
    /** Replace Setup location masters (also mirrors to locationRows). */
    setLocations(state, list) {
      state.locations = Array.isArray(list) ? list : [];
      state.locationRows = state.locations;
    },
    /** Location page table rows. */
    setLocationRows(state, list) {
      state.locationRows = Array.isArray(list) ? list : [];
    },
    /** Location page fetch spinner. */
    setLocationLoading(state, loading) {
      state.locationLoading = !!loading;
    },
    /** Chart of accounts list (Task Library / AR). */
    setChartOfAccounts(state, list) {
      state.chartOfAccounts = Array.isArray(list) ? list : [];
    },
    /** Users list for Task Library assignment pickers. */
    setUsers(state, list) {
      state.users = Array.isArray(list) ? list : [];
    },
    /** Merge form-select metadata (tags, types, thresholds, etc.). */
    setFormMeta(state, meta) {
      state.formMeta = {
        ...state.formMeta,
        ...(meta || {}),
      };
    },
    /** Add/Edit Task Template dialog state (Task Library). */
    setAddTemplateDialog(state, payload) {
      state.addTemplateDialog = { ...emptyAddTaskTemplateDialog(), ...(payload || {}) };
    },
    /** Custom field create/edit dialog state (Task Library). */
    setCustomFieldDialog(state, payload) {
      state.customFieldDialog = { ...emptyCustomFieldDialog(), ...(payload || {}) };
    },
    /** Bulk import dialog open flag (Task Library). */
    setBulkImportOpen(state, open) {
      state.bulkImportOpen = !!open;
    },
    /** Active Task Library task template on the detail page. */
    setActiveTemplate(state, template) {
      state.activeTemplate = template || null;
    },
    /** Task template detail fetch spinner. */
    setActiveTemplateLoading(state, loading) {
      state.activeTemplateLoading = !!loading;
    },
    /** Task template detail page tab. */
    setDetailTab(state, tab) {
      state.detailTab = tab || 'assignments';
    },
    /** Setup Location add/edit dialog state. */
    setLocationDialog(state, payload) {
      state.locationDialog = { ...emptyLocationDialog(), ...(payload || {}) };
    },
    /** Setup Users table rows (raw list API payload). */
    setUserManagementRows(state, list) {
      state.userManagementRows = Array.isArray(list) ? list : [];
    },
    /** Setup Users list fetch spinner. */
    setUserManagementLoading(state, loading) {
      state.userManagementLoading = !!loading;
    },
    /** Full location catalog for Setup Users (enabled + disabled). */
    setUserManagementLocations(state, list) {
      state.userManagementLocations = Array.isArray(list) ? list : [];
    },
    /** Entity users available for Setup Users add dialog. */
    setUserManagementEntityUsers(state, list) {
      state.userManagementEntityUsers = Array.isArray(list) ? list : [];
    },
  },

  getters: {
    /** Task Library task templates filtered to the active Setup cycle type (FBC / RM / IA / OTHERS). */
    taskTemplatesForCycleType: (state) => {
      const cycleType = resolveCycleBucket(state.cycleType);
      return (state.templates || []).filter(
        (t) => resolveCycleBucket(t.cycle_type || t.cycleType) === cycleType
      );
    },
    /** Custom columns filtered to the active Setup cycle type. */
    customColumnsForCycleType: (state) => {
      const cycleType = resolveCycleBucket(state.cycleType);
      return (state.customColumns || []).filter(
        (c) => resolveCycleBucket(c.cycleType || c.cycle_type) === cycleType
      );
    },
    /** Selected Task Library task templates (by selectedTemplateIds). */
    selectedTaskTemplates: (state) => {
      const idSet = new Set(state.selectedTemplateIds || []);
      return (state.templates || []).filter((t) => idSet.has(t._id));
    },
  },

  actions: {
    /** Open the Setup Location add/edit dialog. */
    openLocationDialog({ commit }, { mode = 'add', locationId = null } = {}) {
      commit('setLocationDialog', { open: true, mode, locationId });
    },

    /** Close the Setup Location dialog. */
    closeLocationDialog({ commit }) {
      commit('setLocationDialog', emptyLocationDialog());
    },

    /** Open the Add Task Template dialog (Task Library). */
    openAddTaskTemplateDialog({ commit }, payload = {}) {
      commit('setAddTemplateDialog', { open: true, mode: 'add', ...payload });
    },

    /** Open the Edit Task Template dialog (Task Library). */
    openEditTaskTemplateDialog({ commit }, { templateId, taskType = null } = {}) {
      commit('setAddTemplateDialog', {
        open: true,
        mode: 'edit',
        templateId,
        taskType,
      });
    },

    /** Close the Add/Edit Task Template dialog. */
    closeAddTaskTemplateDialog({ commit }) {
      commit('setAddTemplateDialog', emptyAddTaskTemplateDialog());
    },

    /** Open the custom field create/edit dialog (Task Library). */
    openCustomFieldDialog({ commit }, payload = {}) {
      commit('setCustomFieldDialog', { open: true, mode: 'add', ...payload });
    },

    /** Close the custom field dialog. */
    closeCustomFieldDialog({ commit }) {
      commit('setCustomFieldDialog', emptyCustomFieldDialog());
    },

    /**
     * Fetch locations + thresholds + form selects.
     * Composes threshold onto formMeta for Location page join.
     */
    async fetchLocations({ commit, state }) {
      commit('setLocationLoading', true);
      try {
        const [locRes, thresholdRes, formSelectsRes] = await Promise.all([
          Vue.axios.post(SETUP_API.LOCATION_GET),
          Vue.axios.post(SETUP_API.THRESHOLD_GET),
          Vue.axios.post(SETUP_API.LOCATION_FORMSELECTS),
        ]);

        const locations =
          locRes?.data?.status === 'success' ? locRes.data.data || [] : [];
        const thresholds =
          thresholdRes?.data?.status === 'success'
            ? thresholdRes.data.data || []
            : [];

        let locationTypes = [];
        let countryList = [];
        if (formSelectsRes?.data?.status === 'success') {
          const selects = formSelectsRes.data.data?.[0] || {};
          locationTypes = selects.type || [];
          countryList = (selects.countryList || []).map((item) =>
            typeof item === 'string' ? item : item?.name || item
          );
        }

        commit('setLocations', locations);
        commit('setLocationRows', locations);
        commit('setFormMeta', {
          ...state.formMeta,
          locationTypes,
          countryList,
          thresholds,
        });
        return locations;
      } catch (error) {
        console.error('[setup]: fetchLocations error', error);
        commit('setLocations', []);
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load locations',
          { root: true }
        );
        throw error;
      } finally {
        commit('setLocationLoading', false);
      }
    },

    /**
     * Create location (threshold included in same payload; backend upserts master threshold).
     * payload from locationPayload.buildLocationPayload
     */
    async createLocation({ commit, dispatch }, payload) {
      const locationData = payload?.location || payload || {};

      const locRes = await Vue.axios.post(SETUP_API.LOCATION_ADD, {
        data: locationData,
      });
      const locResult = assertSuccess(locRes, 'Location not added');
      const created = locResult.data?.[0] || locResult.data || locationData;

      commit('sbSuccess', 'Location added successfully', { root: true });
      await dispatch('fetchLocations');
      return created;
    },

    /**
     * Update location (threshold included in same payload; backend upserts master threshold).
     * payload from locationPayload.buildLocationPayload
     */
    async updateLocation({ commit, dispatch }, payload) {
      const locationData = payload?.location || payload || {};
      const locationId = locationData._id;
      if (!locationId) throw new Error('Location id required');

      const { _id, ...rest } = locationData;
      const locRes = await Vue.axios.post(SETUP_API.LOCATION_EDIT(locationId), {
        data: { ...rest, _id },
      });
      assertSuccess(locRes, 'Location not updated');

      commit('sbSuccess', 'Location updated successfully', { root: true });
      await dispatch('fetchLocations');
      return locationId;
    },

    /** Enable a Setup location master. */
    async enableLocation({ commit, dispatch }, id) {
      const res = await Vue.axios.post(SETUP_API.LOCATION_ENABLE(id));
      assertSuccess(res, 'Failed to enable location');
      commit('sbSuccess', 'Location enabled', { root: true });
      await dispatch('fetchLocations');
    },

    /** Disable a Setup location master. */
    async disableLocation({ commit, dispatch }, id) {
      const res = await Vue.axios.post(SETUP_API.LOCATION_DISABLE(id));
      assertSuccess(res, 'Failed to disable location');
      commit('sbSuccess', 'Location disabled', { root: true });
      await dispatch('fetchLocations');
    },

    /**
     * Bootstrap Task Library page data: form selects, COA, locations,
     * then load task templates + custom columns.
     */
    async fetchTaskLibraryBootstrap({ commit, dispatch, state }) {
      commit('setTemplatesInitialLoading', true);
      try {
        // Checklist getformdata projects location name only. LOCATION_GET returns
        // master `type`, which consolidated GL rules need on every bootstrap.
        const [formDataRes, coaRes, locRes] = await Promise.all([
          Vue.axios.post(SETUP_API.CHECKLIST_FORMDATA, checklistFormDataPayload(state.cycleType)),
          Vue.axios.post(SETUP_API.COA_GET),
          Vue.axios.post(SETUP_API.LOCATION_GET),
        ]);

        let coaList = [];
        if (coaRes?.data?.status === 'success') {
          coaList = coaRes.data.data || [];
          commit('setChartOfAccounts', coaList);
        }

        const locationRecords =
          locRes?.data?.status === 'success' ? locRes.data.data || [] : [];

        if (formDataRes?.data?.status === 'success') {
          const fd = unwrapFormDataPayload(formDataRes.data.data || formDataRes.data);
          const users = fd.users || fd.userlist || [];
          commit('setUsers', users);

          // LOCATION_GET includes master `type`. Formdata only projects name.
          if (locationRecords.length) {
            commit(
              'setLocations',
              mergeLocationTypes(
                locationRecords,
                Array.isArray(fd.location) ? fd.location : []
              )
            );
          } else if (Array.isArray(fd.location) && fd.location.length) {
            commit('setLocations', fd.location);
          }

          commit('setFormMeta', {
            tags: Array.isArray(fd.tags) ? fd.tags : [],
            assertions: fd.assertions || fd.possibleassertions || [],
            ...categoryMetaFromFormData(fd, coaList),
          });
        } else if (coaList.length) {
          commit('setFormMeta', categoryMetaFromFormData({}, coaList));
          if (locRes?.data?.status === 'success') {
            commit('setLocations', locationRecords);
          }
        } else if (locRes?.data?.status === 'success') {
          commit('setLocations', locationRecords);
        }

        await Promise.all([
          dispatch('fetchTaskTemplates'),
          dispatch('fetchCustomColumns'),
        ]);

        // Frequency options: unique tags from loaded templates (API does not return tag master).
        const derivedTags = uniqueTaskTemplateTags(state.templates);
        if (derivedTags.length) {
          commit('setFormMeta', {
            tags: derivedTags,
          });
        }
      } catch (error) {
        console.error('[setup]: fetchTaskLibraryBootstrap error', error);
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load Task Library',
          { root: true }
        );
        throw error;
      } finally {
        commit('setTemplatesInitialLoading', false);
      }
    },

    /**
     * Reload checklist category lists after a task create/edit.
     * Same idea as cycle task save refreshing v3 getformdata.
     */
    async refreshChecklistFormCategories({ commit, state }) {
      try {
        const formDataRes = await Vue.axios.post(
          SETUP_API.CHECKLIST_FORMDATA,
          checklistFormDataPayload(state.cycleType)
        );
        assertSuccess(formDataRes, 'Failed to refresh categories');
        const fd = unwrapFormDataPayload(formDataRes.data.data || formDataRes.data);
        commit('setFormMeta', categoryMetaFromFormData(fd, state.chartOfAccounts));
      } catch (error) {
        console.error('[setup]: refreshChecklistFormCategories error', error);
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to refresh categories',
          { root: true }
        );
        throw error;
      }
    },

    /** Load all Setup Task Library task templates (master checklist). */
    async fetchTaskTemplates({ commit }) {
      commit('setTemplatesLoading', true);
      try {
        const response = await Vue.axios.post(SETUP_API.CHECKLIST_GET);
        const list =
          response?.data?.status === 'success' ? response.data.data || [] : [];
        commit('setTemplates', list);
        commit('setTemplateCountsByCycleType', buildCycleCounts(list));
        return list;
      } catch (error) {
        console.error('[setup]: fetchTaskTemplates error', error);
        commit('setTemplates', []);
        commit('setTemplateCountsByCycleType', emptyCycleCounts());
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load templates',
          { root: true }
        );
        throw error;
      } finally {
        commit('setTemplatesLoading', false);
      }
    },

    /** Create a Setup Task Library task template. */
    async createTaskTemplate({ commit, dispatch }, data) {
      try {
        const res = await Vue.axios.post(SETUP_API.CHECKLIST_ADD, { data });
        assertSuccess(res, 'Failed to create template');
        commit('sbSuccess', 'Template added', { root: true });
        await dispatch('fetchTaskTemplates');
        await refreshCategoriesAfterWrite(dispatch);
        return res.data;
      } catch (error) {
        const fieldErrors = error?.response?.data?.data?.fieldErrors;
        if (!isDueOnlyFieldError(fieldErrors)) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to create template';
          commit('sbError', message, { root: true });
        }
        throw error;
      }
    },

    /** Update a Setup Task Library task template. */
    async updateTaskTemplate({ commit, dispatch }, { id, data }) {
      try {
        const res = await Vue.axios.post(SETUP_API.CHECKLIST_EDIT(id), { data });
        assertSuccess(res, 'Failed to update template');
        commit('sbSuccess', 'Task template updated', { root: true });
        await dispatch('fetchTaskTemplates');
        await refreshCategoriesAfterWrite(dispatch);
        return res.data;
      } catch (error) {
        const fieldErrors = error?.response?.data?.data?.fieldErrors;
        if (!isDueOnlyFieldError(fieldErrors)) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to update template';
          commit('sbError', message, { root: true });
        }
        throw error;
      }
    },

    /** Enable a Setup Task Library task template. */
    async enableTaskTemplate({ commit, dispatch }, id) {
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_ENABLE(id));
      assertSuccess(res, 'Failed to enable template');
      commit('sbSuccess', res.data?.message || 'Checklist item enabled successfully', { root: true });
      await dispatch('fetchTaskTemplates');
    },

    /** Disable a Setup Task Library task template. */
    async disableTaskTemplate({ commit, dispatch }, id) {
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_DISABLE(id));
      assertSuccess(res, 'Failed to disable template');
      commit('sbSuccess', res.data?.message || 'Checklist item disabled successfully', { root: true });
      await dispatch('fetchTaskTemplates');
    },

    /** Bulk-edit Setup Task Library task templates (Excel bulk update). */
    async updateBulkTaskTemplates({ commit, dispatch, rootState }, payload) {
      const body = {
        client: rootState.client || rootState.user?.client || undefined,
        ...(payload || {}),
      };
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_BULK_UPDATE, body);
      if (res?.data?.status !== 'success') {
        const error = new Error(res?.data?.message || 'Bulk update failed');
        error.rowErrors = res?.data?.data?.rowErrors || [];
        error.response = res;
        commit('sbError', error.message, { root: true });
        throw error;
      }
      commit('sbSuccess', res.data.message || 'Templates updated', { root: true });
      await dispatch('fetchTaskTemplates');
      await refreshCategoriesAfterWrite(dispatch);
      return res.data;
    },

    /** Bulk-import new Setup Task Library task templates (Excel bulk add). */
    async importBulkTaskTemplates({ commit, dispatch, rootState }, payload) {
      const body = {
        client: rootState.client || rootState.user?.client || undefined,
        ...(payload || {}),
      };
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_BULK_ADD, body);
      if (res?.data?.status !== 'success') {
        const error = new Error(res?.data?.message || 'Bulk import failed');
        error.rowErrors = res?.data?.data?.rowErrors || [];
        error.response = res;
        commit('sbError', error.message, { root: true });
        throw error;
      }
      commit('sbSuccess', res.data.message || 'Templates imported', { root: true });
      await dispatch('fetchTaskTemplates');
      await refreshCategoriesAfterWrite(dispatch);
      return res.data;
    },

    /**
     * Dry-run bulk create/edit validation for Task Library task templates.
     * Pass mode: "create" | "update" (pipeline vocabulary). Defaults to create.
     * Returns API envelope even when valid=false.
     */
    async validateBulkTaskTemplates({ rootState }, payload) {
      const { mode, ...rest } = payload || {};
      const body = {
        client: rootState.client || rootState.user?.client || undefined,
        mode: mode === 'update' ? 'update' : 'create',
        ...rest,
      };
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_BULK_VALIDATE, body);
      return res?.data || {};
    },

    /**
     * Dry-run bulk edit validation for Task Library task templates.
     * Returns API envelope even when valid=false.
     */
    async validateBulkEditTaskTemplates({ rootState }, payload) {
      const body = {
        client: rootState.client || rootState.user?.client || undefined,
        mode: 'update',
        ...(payload || {}),
      };
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_BULK_VALIDATE, body);
      return res?.data || {};
    },

    /** Bulk assign/replace users on selected Task Library task templates. */
    async manageUsersForBulkTaskTemplate({ commit, dispatch }, payload) {
      const { silent, data: rawData, ...rest } = payload || {};
      const raw = rawData || {};
      const action = raw.action || raw.type;
      const requestData = {
        _id: Array.isArray(raw._id) ? raw._id.map(String) : raw._id,
        action,
        usertype: String(raw.usertype || '').toLowerCase(),
      };

      if (action === 'associate') {
        requestData.changeto = raw.changeto !== undefined && raw.changeto !== null
          ? raw.changeto
          : raw.user;
      } else if (action === 'remove' || action === 'move') {
        requestData.user = raw.user || raw.userid;
      } else if (action === 'replace') {
        requestData.user = raw.user || raw.fromuserid;
        requestData.changeto = raw.changeto || raw.touserid;
      }

      const requestPayload = { ...rest, data: requestData };
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_MANAGE_USER, requestPayload);
      assertSuccess(res, 'User update failed');
      if (!silent) {
        commit('sbSuccess', res.data.message || 'Users updated', { root: true });
        await dispatch('fetchTaskTemplates');
      }
      return res.data;
    },

    /** Bulk update due days on selected Task Library task templates. */
    async updateDueDaysForBulkTaskTemplate({ commit, dispatch }, { ids, duedays }) {
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_BULK_DUE_DAYS, {
        data: { duedays, _id: ids },
      });
      assertSuccess(res, 'Due days update failed');
      commit('sbSuccess', res.data.message || 'Due days updated', { root: true });
      await dispatch('fetchTaskTemplates');
      return res.data;
    },

    /** Bulk update Enforce Threshold (skipValidations) on selected task templates. */
    async updateSkipValidationsForBulkTaskTemplate({ commit, dispatch, state }, { ids, enforceThreshold }) {
      const selected = ids || [];
      const data = selected
        .map((id) => ({
          ref: templateRefFromSelection(state.templates, id),
          enforceThreshold: !!enforceThreshold,
        }))
        .filter((row) => row.ref);

      const res = await Vue.axios.post(SETUP_API.CHECKLIST_BULK_UPDATE, { data });
      assertSuccess(res, 'Threshold update failed');
      commit('sbSuccess', 'Enforce Threshold updated', { root: true });
      await dispatch('fetchTaskTemplates');
      return res.data;
    },

    /** Bulk assign/replace locations on selected Task Library task templates. */
    async manageLocationsForBulkTaskTemplate({ commit, dispatch }, payload) {
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_MANAGE_LOCATION, payload);
      assertSuccess(res, 'Location update failed');
      commit('sbSuccess', res.data.message || 'Locations updated', { root: true });
      await dispatch('fetchTaskTemplates');
      return res.data;
    },

    /** Load custom columns for all cycle types (client filters by active cycle). */
    async fetchCustomColumns({ commit }) {
      commit('setCustomFieldsLoading', true);
      try {
        const response = await Vue.axios.post(SETUP_API.READ_COLUMN, {
          query: { deleted: false },
        });
        const columns =
          response?.data?.status === 'success' ? response.data.data || [] : [];
        commit('setCustomColumns', columns);
        commit('setCustomFieldCountsByCycleType', buildCycleCounts(columns));
        return columns;
      } catch (error) {
        console.error('[setup]: fetchCustomColumns error', error);
        commit('setCustomColumns', []);
        commit('setCustomFieldCountsByCycleType', emptyCycleCounts());
        throw error;
      } finally {
        commit('setCustomFieldsLoading', false);
      }
    },

    /** Create a Task Library custom field column. */
    async createCustomColumn({ commit, dispatch }, columnDetails) {
      const res = await Vue.axios.post(SETUP_API.CREATE_COLUMN, { columnDetails });
      assertSuccess(res, 'Failed to create custom field');
      commit('sbSuccess', 'Custom field created', { root: true });
      await dispatch('fetchCustomColumns');
      return res.data;
    },

    /** Update a Task Library custom field column. */
    async editCustomColumn({ commit, dispatch }, { data, query }) {
      const res = await Vue.axios.post(SETUP_API.EDIT_COLUMN, { data, query });
      assertSuccess(res, 'Failed to update custom field');
      commit('sbSuccess', 'Custom field updated', { root: true });
      await dispatch('fetchCustomColumns');
      return res.data;
    },

    /**
     * Picker rows for Setup template dependency editor (NXTGEN-6833).
     * @param {object} _ctx
     * @param {{ duedays: number|string, excludeTaskId?: string, page?: number, search?: string }} payload
     */
    async fetchTemplatesForLink(_ctx, payload = {}) {
      const body = {
        duedays: payload.duedays,
        excludeTaskId: payload.excludeTaskId || undefined,
        pagination: {
          page: payload.page || 1,
          limit: 20,
        },
        search: payload.search || '',
        searchFields: ['ref', 'description', 'locationName'],
      };
      const res = await Vue.axios.post(SETUP_API.CHECKLIST_GET_TASKS_FOR_LINK, body);
      if (res?.data?.status !== 'success') {
        const message =
          res?.data?.message || 'Failed to load tasks for link';
        throw new Error(message);
      }
      const data = res.data.data || {};
      return {
        records: data.records || [],
        totalCount: data.totalCount || 0,
        page: data.page || body.pagination.page,
        totalPages: data.totalPages || 1,
      };
    },

    /** Load one Setup Task Library task template for the detail page. */
    async fetchTaskTemplateDetail({ commit }, id) {
      commit('setActiveTemplateLoading', true);
      // Drop previous row immediately so a new click never shows stale detail.
      commit('setActiveTemplate', null);
      try {
        const idStr = id != null ? String(id) : '';
        if (!idStr) {
          commit('setActiveTemplate', null);
          return null;
        }

        const matchById = (item) => {
          if (!item) return false;
          const raw = item._id;
          if (raw == null) return false;
          if (typeof raw === 'object') {
            return String(raw._id || raw.$oid || raw.id || '') === idStr;
          }
          return String(raw) === idStr;
        };

        const pickFromPayload = (data) => {
          if (Array.isArray(data)) {
            // Never default to data[0] — that showed the wrong template when
            // get/:id incorrectly returned the full list.
            return data.find(matchById) || null;
          }
          return matchById(data) ? data : null;
        };

        // Prefer get-by-id then fallback to list filter
        let template = null;
        try {
          const one = await Vue.axios.post(SETUP_API.CHECKLIST_GET_ONE(idStr));
          if (one?.data?.status === 'success') {
            template = pickFromPayload(one.data.data);
          }
        } catch (_) {
          /* fallback below */
        }
        if (!template) {
          const listRes = await Vue.axios.post(SETUP_API.CHECKLIST_GET);
          const list =
            listRes?.data?.status === 'success' ? listRes.data.data || [] : [];
          template = list.find(matchById) || null;
        }

        if (template) {
          try {
            const detailRes = await Vue.axios.post(
              SETUP_API.CHECKLIST_GET_DETAILS(idStr),
              {}
            );
            if (detailRes?.data?.status === 'success' && detailRes.data.data) {
              const detail = detailRes.data.data;
              template = {
                ...template,
                blocking: detail.blocking || [],
                blockedBy: detail.blockedBy || [],
                relates: detail.relates || [],
              };
            }
          } catch (linkErr) {
            console.log('[setup]: fetchTaskTemplateDetail getdetails', linkErr?.message || linkErr);
          }
        }

        commit('setActiveTemplate', template);
        return template;
      } catch (error) {
        console.error('[setup]: fetchTaskTemplateDetail error', error);
        commit('setActiveTemplate', null);
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load template',
          { root: true }
        );
        throw error;
      } finally {
        commit('setActiveTemplateLoading', false);
      }
    },

    /**
     * Setup Users — list user-management rows.
     * POST USER_MANAGEMENT_LIST (userConstants.API.LIST).
     */
    async fetchUserManagement({ commit }) {
      commit('setUserManagementLoading', true);
      try {
        const response = await Vue.axios.post(SETUP_API.USER_MANAGEMENT_LIST, {});
        const result = assertSuccess(response, 'Failed to load users.');
        const data = result.data;
        const rows = Array.isArray(data) ? data : [];
        commit('setUserManagementRows', rows);
        return rows;
      } catch (error) {
        console.error('[setup]: fetchUserManagement error', error);
        commit('setUserManagementRows', []);
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load users.',
          { root: true }
        );
        throw error;
      } finally {
        commit('setUserManagementLoading', false);
      }
    },

    /**
     * Setup Users — get one user-management row by id.
     * POST USER_MANAGEMENT_GET (userConstants.API.GET).
     */
    async getUserManagement(_context, id) {
      if (!id) throw new Error('User id required');
      const response = await Vue.axios.post(SETUP_API.USER_MANAGEMENT_GET(id));
      const data = response?.data?.data;
      const row = Array.isArray(data) ? data[0] : data;
      return row || null;
    },

    /**
     * Setup Users — create user-management row(s).
     * POST USER_MANAGEMENT_ADD (userConstants.API.ADD).
     * @param {Object|Array} payload — `{ data: [user] }` or a single user object
     */
    async addUserManagement({ commit, dispatch }, payload) {
      const body =
        payload && typeof payload === 'object' && Array.isArray(payload.data)
          ? payload
          : { data: Array.isArray(payload) ? payload : [payload] };
      const response = await Vue.axios.post(SETUP_API.USER_MANAGEMENT_ADD, body);
      assertSuccess(response, 'Failed to add user.');
      commit('sbSuccess', response.data?.message || 'User added successfully', { root: true });
      await dispatch('fetchUserManagement');
      return response.data;
    },

    /**
     * Setup Users — edit user-management row(s).
     * POST USER_MANAGEMENT_EDIT (userConstants.API.EDIT).
     * @param {{ id: string, payload: Object|Array }} args
     */
    async editUserManagement({ commit, dispatch }, { id, payload } = {}) {
      if (!id) throw new Error('User id required');
      const body =
        payload && typeof payload === 'object' && Array.isArray(payload.data)
          ? payload
          : { data: Array.isArray(payload) ? payload : [payload] };
      const response = await Vue.axios.post(
        SETUP_API.USER_MANAGEMENT_EDIT(id),
        body
      );
      assertSuccess(response, 'Failed to save user.');
      commit('sbSuccess', response.data?.message || 'User saved successfully', { root: true });
      await dispatch('fetchUserManagement');
      return response.data;
    },

    /**
     * Setup Users — set enable/disable status.
     * POST USER_MANAGEMENT_SET_STATUS (userConstants.API.SET_STATUS).
     */
    async setUserManagementStatus({ dispatch }, { id, status } = {}) {
      if (!id) throw new Error('User id required');
      const response = await Vue.axios.post(
        SETUP_API.USER_MANAGEMENT_SET_STATUS(id),
        { status }
      );
      assertSuccess(response, 'Failed to update user status.');
      await dispatch('fetchUserManagement');
      return response.data;
    },

    /**
     * Setup Users — full location catalog (enabled + disabled).
     * Same endpoint as Setup Location LOCATION_GET; does not touch
     * tasks.enabledLocations.
     */
    async fetchUserManagementLocations({ commit }) {
      try {
        const response = await Vue.axios.post(SETUP_API.LOCATION_GET);
        const result = assertSuccess(response, 'Failed to load locations.');
        const data = result.data || [];
        const list = Array.isArray(data) ? data : [];
        commit('setUserManagementLocations', list);
        return list;
      } catch (error) {
        console.error('[setup]: fetchUserManagementLocations error', error);
        commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load locations.',
          { root: true }
        );
        throw error;
      }
    },

    /**
     * Setup Users — entity users for the add-user picker.
     * POST ENTITY_USERS (userConstants.API.ENTITY_USERS).
     */
    async fetchEntityUsersForAdd({ commit }, params = {}) {
      try {
        const response = await Vue.axios.post(SETUP_API.ENTITY_USERS, {
          usersStatus: true,
          ...params,
        });
        const data = response?.data?.data ?? response?.data;
        const list = Array.isArray(data) ? data : [];
        commit('setUserManagementEntityUsers', list);
        return list;
      } catch (error) {
        console.error('[setup]: fetchEntityUsersForAdd error', error);
        commit('setUserManagementEntityUsers', []);
        throw error;
      }
    },
  },
};
